import { authenticate } from "@/models/socket/authenticate.model";
import { SocketCom } from "@/types";
import { getLSRefreshToken } from "@/utils/tokens.util";
import { getUserLanguage } from "@/utils/utils.util";
import { io, Socket } from "socket.io-client";
import Swal from "sweetalert2";

class SocketController {
  private static socket: Socket;
  private readonly serverUrl: string;
  private socketStatus: "connected" | "connecting" | "disconnected" =
    "disconnected";
  private handlers: { [key: string]: (result: any) => void } = {};
  private readonly MAX_RECONNECT_ATTEMPTS = 100;
  private reconnectAttempts = 0;

  constructor() {
    this.serverUrl = import.meta.env.VITE_SOCKET_SERVER_URL || "";
    if (this.serverUrl === "") {
      Swal.fire({
        icon: "error",
        title: `Invalid Server URL`,
        html: `<strong class="font-extrabold">Please contact support</strong><br /><br />Server URL is empty`,
        showConfirmButton: true,
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none",
        },
      });
      console.error("Server URL is empty");
    }

    SocketController.socket = io(this.serverUrl, {
      // timeout: 60000,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });
    this.connect();
    this.initializeListeners();
  }

  private initializeListeners() {
    SocketController.socket.on("messages", (response: SocketCom) => {
      console.log("Received message:", response);
      if (response.command && this.handlers[response.command]) {
        console.log("Command: " + response.command);
        // console.log(`Is valid command: ${!!this.handlers[response.command]}`);
        // Directly pass validated and structured data to the component
        this.handlers[response.command](
          this.structureDataBasedOnCommand(response)
        );

        // // Notify about the error
        // this.notifyComponents(response.command, {
        //   rStatus: "error",
        //   rData: "Invalid response format for SocketCom"
        // });
      }
      // else {
      //   // can happen if the listener is not initialized at the time of the response
      //   console.error(
      //     "can happen if the listener is not initialized at the time of the response"
      //   );
      //   console.error("Unhandled command or invalid response", response);
      //   console.error("Command: " + response.command);
      //   console.error(`Is valid command: ${!!this.handlers[response.command]}`);
      // }
    });
  }

  // Connects to the server
  private connect(): void {
    SocketController.socket.on("connect", () => {
      console.log("Socket connected");
      this.socketStatus = "connected";
      const refreshToken = getLSRefreshToken();
      if (refreshToken) {
        authenticate(refreshToken);
      }
      if (this.reconnectAttempts > 0) {
        this.reconnectAttempts = 0;
      }
    });

    SocketController.socket.on("disconnect", () => {
      console.log("Disconnected");
      this.attemptReconnect();
    });
  }

  private firstDisconnectTime: number = 0;

  private attemptReconnect() {
    if (this.firstDisconnectTime === 0) {
      this.firstDisconnectTime = Date.now();
    }
    if (this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
      setTimeout(
        () => {
          console.log(
            `Attempting to reconnect (${this.reconnectAttempts + 1}/${
              this.MAX_RECONNECT_ATTEMPTS
            })`
          );
          SocketController.socket.connect();
          this.reconnectAttempts++;
          // calculate time since first disconnect in seconds
          const timeSinceFirstDisconnect =
            (Date.now() - this.firstDisconnectTime) / 1000;
          console.log(
            "Time since first disconnect: " + timeSinceFirstDisconnect
          );
        },
        1000 * Math.pow(2, this.reconnectAttempts)
      ); // Exponential backoff strategy
    } else {
      console.log(
        "Max reconnect attempts reached. Please check your connection and try again."
      );
      // Notify the user or take additional steps
    }
  }

  public get status(): "connected" | "connecting" | "disconnected" {
    console.log(SocketController.socket);
    console.log("Socket connected: " + SocketController.socket.connected);
    console.log("Socket status: " + this.socketStatus);
    return this.socketStatus;
  }

  // General method to send commands with validation. Private as it should not be called directly
  public async sendCommand(
    command: SocketCom["command"],
    data: object = {},
    requireResponse: boolean
  ): Promise<SocketCom> {
    return new Promise((resolve, reject) => {
      const lang = getUserLanguage();
      data = { ...data, lanIso: lang };

      // Set a timeout to reject the promise if not resolved within TIMEOUT_DURATION
      const requestTimeout = setTimeout(() => {
        console.log("Request timed out for command " + command);

        Swal.fire({
          icon: "error",
          title: `Network Timeout`,
          html: `<strong class="font-extrabold">Please check your internet connection<br />and try reloading the page</strong>`,
          // reload page button
          showConfirmButton: true,
          confirmButtonText: "Reload",
          customClass: {
            popup: "!rounded-lg",
            confirmButton:
              "!bg-primary !outline-none !border-none !shadow-none",
          },
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.reload();
          }
        });

        resolve({ command, status: 0, data: "Request timed out" });
      }, 20000);
      console.log("Sending command: " + command);
      console.log("Sending data:");
      console.log(data);
      // Basic validation for data structure can be added here if needed
      //["messages", {command: "magicLink", status: 202, data: {message: "Email Sent"}}]
      if (requireResponse) {
        try {
          SocketController.socket.emit(
            "messages",
            { command, data },
            (response: SocketCom) => {
              //!DELETE ME
              if (response) {
                console.log("Socket response:", response);
              }
              if (response.status && typeof response.status === "number") {
                clearTimeout(requestTimeout);
                resolve(response);
              } else {
                console.error(
                  "Invalid response format for SocketCom",
                  response
                );
                clearTimeout(requestTimeout);
                reject(new Error("Error with command " + command));
              }
            }
          );
        } catch (error) {
          console.error("Error occurred in socket emit", error);
          clearTimeout(requestTimeout);
          reject(new Error("Error with Socket send command " + command));
        }
      } else {
        SocketController.socket.emit("messages", { command, data });
        clearTimeout(requestTimeout);
        resolve({ command, status: 202, data: "Command sent" });
      }
    });
  }

  private structureDataBasedOnCommand(response: SocketCom): {
    rStatus: "success" | "error";
    rData: any; // validated and structured data
  } {
    // This method should structure the data according to the command
    // For example, for "last-messages", it might extract and format message data
    // Placeholder implementation:
    // console.log(response.command);

    // Add command listeners here like this one
    // if (response.command === "magicLink") {
    //   return {
    //     rStatus: "success",
    //     rData: response.data,
    //   };
    // }

    return {
      rStatus: "error",
      rData: "Invalid command",
    };
  }

  // Method to notify components based on the command
  private notifyComponents(command: string, data: any) {
    if (this.handlers[command]) {
      this.handlers[command](data);
    }
  }

  public on(command: string, handler: (result: any) => void): void {
    this.handlers[command] = handler;
  }

  public off(command: string): void {
    delete this.handlers[command];
  }

  // Disconnect the socket
  public disconnect(): void {
    SocketController.socket.disconnect();
  }
}

export default SocketController;
